import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import moment from 'moment';

import PageContainer from '../../components/layout/PageContainer';
import { BTSessionStatus } from '../../constants';
import { EvvHistoryDetails, EvvSessionTable } from './components';
import { EVV_HISTORY_LOOKBACK_DAYS } from './constants';
import { useEvvHistory } from './hooks';
import { FILTERS_SX } from './styles';

function EvvHistory() {
  const {
    sessions,
    loading,
    selectedSessionId,
    history,
    historyLoading,
    historyError,
    selectSession,
    reload,
    sessionError,
  } = useEvvHistory();
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('all');
  const [evvFilter, setEvvFilter] = useState('all');
  const [startDate, setStartDate] = useState(
    moment().subtract(EVV_HISTORY_LOOKBACK_DAYS, 'days').format('YYYY-MM-DD'),
  );
  const [endDate, setEndDate] = useState(moment().format('YYYY-MM-DD'));
  const filteredSessions = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return sessions.filter(
      (session) =>
        (status === 'all' || session.status === status) &&
        (evvFilter !== 'required' || session.evv_required) &&
        (evvFilter !== 'missing' ||
          (session.evv_required && (!session.evv_start_captured || !session.evv_end_captured))) &&
        (evvFilter !== 'captured' || (session.evv_start_captured && session.evv_end_captured)) &&
        (!normalizedQuery ||
          [
            session.id,
            session.client_id,
            `${session.client?.first_name ?? ''} ${session.client?.last_name ?? ''}`,
            `${session.session_provider?.first_name ?? ''} ${session.session_provider?.last_name ?? ''}`,
          ].some((value) => String(value).toLowerCase().includes(normalizedQuery))),
    );
  }, [query, sessions, status, evvFilter]);

  function searchSessions() {
    reload(startDate, endDate);
  }

  function refreshHistory() {
    selectSession(selectedSessionId);
  }

  return (
    <PageContainer dataTestId="evvHistoryPage">
      <Typography variant="h4" sx={{ mt: 3, mb: 2 }}>
        EVV History
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        Review session capture status, token history, and capture failures. Dates use your local
        time zone. Results show UTC.
      </Typography>
      <Box sx={FILTERS_SX}>
        <TextField
          label="Search ID, client, or provider"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          size="small"
        />
        <TextField
          type="date"
          label="From"
          value={startDate}
          onChange={(event) => setStartDate(event.target.value)}
          size="small"
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          type="date"
          label="To"
          value={endDate}
          onChange={(event) => setEndDate(event.target.value)}
          size="small"
          InputLabelProps={{ shrink: true }}
        />
        <Button variant="contained" onClick={searchSessions}>
          Search
        </Button>
        <TextField
          select
          label="Session status"
          value={status}
          onChange={(event) => setStatus(event.target.value)}
          size="small"
        >
          <MenuItem value="all">All statuses</MenuItem>
          {Object.values(BTSessionStatus).map((sessionStatus) => (
            <MenuItem key={sessionStatus} value={sessionStatus}>
              {sessionStatus}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="EVV status"
          value={evvFilter}
          onChange={(event) => setEvvFilter(event.target.value)}
          size="small"
        >
          <MenuItem value="all">All sessions</MenuItem>
          <MenuItem value="required">EVV required</MenuItem>
          <MenuItem value="missing">Missing required capture</MenuItem>
          <MenuItem value="captured">Both captured</MenuItem>
        </TextField>
      </Box>
      {loading && <CircularProgress size={24} aria-label="Load sessions" />}
      {sessionError && <Alert severity="error">{sessionError}</Alert>}
      {!loading && !sessionError && !filteredSessions.length && (
        <Alert severity="info">No sessions found</Alert>
      )}
      {!loading && !sessionError && !!filteredSessions.length && (
        <EvvSessionTable
          sessions={filteredSessions}
          selectedSessionId={selectedSessionId}
          onSelect={selectSession}
        />
      )}
      {selectedSessionId !== null && (
        <Card sx={{ mt: 3, flexShrink: 0 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="h6">Session {selectedSessionId}</Typography>
              <Button onClick={refreshHistory} disabled={historyLoading}>
                Refresh details
              </Button>
            </Box>
            {historyLoading && (
              <CircularProgress size={24} sx={{ mt: 2 }} aria-label="Load EVV history" />
            )}
            {historyError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {historyError}
              </Alert>
            )}
            {history && <EvvHistoryDetails history={history} />}
          </CardContent>
        </Card>
      )}
    </PageContainer>
  );
}

export default EvvHistory;
